import type { ErrorHandler as AngularErrorHandler } from '@angular/core';
import type { Router } from '@angular/router';
import { environment } from '@environments/environment';

/**
 * Deferred Sentry client.
 *
 * `@sentry/angular` costs ~271 kB raw (replay alone is 123 kB). Importing it
 * statically put all of it on the critical path of every route — including the
 * public partners landing page, which has no use for it. This module is the one
 * place that touches the SDK, and it does so through a dynamic `import()`, so
 * the bundler emits it as its own chunk fetched after bootstrap.
 *
 * Nothing is lost during the gap: calls made before the SDK lands are buffered
 * and replayed against it, in order, as soon as it is in memory.
 */
type SentryApi = typeof import('@sentry/angular');

/** A call parked until the SDK is available. */
type DeferredCall = (sentry: SentryApi) => void;

/**
 * Ceiling on parked calls. The buffer only ever holds the handful of events
 * raised in the few hundred ms before the SDK resolves; a cap keeps a pathological
 * error loop during that window from growing the array without bound.
 */
const MAX_BUFFERED_CALLS = 50;

let sentryApi: SentryApi | null = null;
let loadAttempt: Promise<SentryApi | null> | null = null;
/** Set when Sentry is switched off or failed to load — stop buffering, fall back to the console. */
let unavailable = false;
let errorHandlerDelegate: AngularErrorHandler | null = null;
const bufferedCalls: DeferredCall[] = [];

/**
 * Holds the `TraceService` instance for the lifetime of the app. It hooks the
 * router in its constructor and its subscription is the only thing keeping it
 * alive, so the reference must be parked somewhere that outlives the caller.
 */
let routerTracing: unknown = null;

/**
 * Fetch and initialize the SDK. Safe to call repeatedly — the first call wins
 * and later callers await the same attempt. Resolves to `null` when Sentry is
 * disabled for this environment or the chunk could not be fetched.
 */
export function startSentry(): Promise<SentryApi | null> {
  loadAttempt ??= loadAndInit();
  return loadAttempt;
}

async function loadAndInit(): Promise<SentryApi | null> {
  // Skip the download entirely when Sentry is off (local/dev): a disabled
  // `init()` would send nothing anyway.
  if (!environment.sentry.enabled) {
    markUnavailable();
    return null;
  }

  try {
    const sentry = await import('@sentry/angular');

    sentry.init({
      dsn: environment.sentry.dsn,
      enabled: environment.sentry.enabled,
      environment: environment.sentry.environment,
      release: environment.version,
      integrations: [sentry.browserTracingIntegration(), sentry.replayIntegration()],
      tracePropagationTargets: [environment.apiUrl],
      tracesSampleRate: environment.production ? 0.05 : 1.0,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 0.5,
      enableLogs: true,
      beforeSend(event, hint) {
        const error = hint.originalException;
        // Drop non-actionable browser noise
        if (error instanceof TypeError && error.message === 'Failed to fetch') return null;
        if (error instanceof TypeError && error.message === 'Load failed') return null;
        return event;
      },
    });

    sentryApi = sentry;
    // Built before the flush so buffered unhandled errors go through the real
    // Sentry handler rather than a second-class path.
    errorHandlerDelegate = sentry.createErrorHandler();

    for (const call of bufferedCalls.splice(0)) {
      call(sentry);
    }

    return sentry;
  } catch (error) {
    markUnavailable();
    console.error('Sentry failed to load — error reporting is disabled for this session.', error);
    return null;
  }
}

function markUnavailable(): void {
  unavailable = true;
  bufferedCalls.length = 0;
}

/**
 * Run `call` against the SDK, or park it until the SDK arrives. Dropped silently
 * once Sentry is known to be unavailable — reporting must never break the app.
 */
export function withSentry(call: DeferredCall): void {
  if (sentryApi) {
    call(sentryApi);
    return;
  }
  if (unavailable || bufferedCalls.length >= MAX_BUFFERED_CALLS) return;
  bufferedCalls.push(call);
}

/** Report a handled error. Mirrors `Sentry.captureException`. */
export function captureException(
  error: unknown,
  context?: Parameters<SentryApi['captureException']>[1],
): void {
  withSentry((sentry) => sentry.captureException(error, context));
}

/**
 * Report a noteworthy condition that is not an exception. Mirrors
 * `Sentry.captureMessage`.
 */
export function captureMessage(
  message: string,
  context?: Parameters<SentryApi['captureMessage']>[1],
): void {
  withSentry((sentry) => sentry.captureMessage(message, context));
}

/**
 * Route an unhandled error through Sentry's own Angular `ErrorHandler`, so the
 * extraction and dialog behavior stays exactly what `createErrorHandler()` does.
 * Falls back to the console while the SDK is missing, so errors are never silent.
 */
export function reportUnhandledError(error: unknown): void {
  if (errorHandlerDelegate) {
    errorHandlerDelegate.handleError(error);
    return;
  }
  if (unavailable || bufferedCalls.length >= MAX_BUFFERED_CALLS) {
    console.error(error);
    return;
  }
  bufferedCalls.push((sentry) => {
    errorHandlerDelegate ??= sentry.createErrorHandler();
    errorHandlerDelegate.handleError(error);
  });
}

/**
 * Start router performance tracing once the SDK is loaded.
 *
 * Trade-off of deferring: the span for the very first navigation is missed,
 * because `TraceService` only starts observing when it is constructed.
 */
export function startRouterTracing(router: Router): void {
  withSentry((sentry) => {
    routerTracing ??= new sentry.TraceService(router);
  });
}
